import { action } from "typesafe-actions";
import { IBusinessPolicyCoverageExclusion } from "./businessPolicyCoverageExclusions.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { ERROR_MESSAGES } from "../../../constants/enums";
import {
  buildBinderRequest,
  BinderScopeArg,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs
// Save-in-progress guard for the lead-binder coverage-exclusions save (Requirement 3.8).
// While a lead-scoped save is in flight, a second save for the same lead is rejected
// until the in-progress save resolves (success or failure). The customer-policy path is
// unaffected — it does not consult this guard.
let leadCoverageExclusionSaveInFlight = false;

export const FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_FAILED";

export const fetchBusinessPolicyCoverageExclusionsListProgress = () =>
  action(FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_PROGRESS);
export const fetchBusinessPolicyCoverageExclusionsListSuccess = (
  data: IBusinessPolicyCoverageExclusion[]
) => action(FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_SUCCESS, { data });
export const fetchBusinessPolicyCoverageExclusionsListFailed = () =>
  action(FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSIONS_LIST_FAILED);

export const fetchBusinessPolicyCoverageExclusionsListAsync =
  (
    customerPolicyId: number,
    status?: string,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyCoverageExclusionsListProgress());

      // Feature: lead-binder-business-tabs
      // When a lead_id is supplied, fetch by lead_id via the centralized endpoint map
      // and never key by customer_policy_id (Requirements 3.1, 3.4). The customer-policy
      // path below is byte-for-byte unchanged (Requirement 4.2).
      const scope: BinderScopeArg = leadId
        ? { kind: "lead", leadId }
        : { kind: "policy", customerPolicyId };

      let finalUrl = buildBinderRequest(
        "coverage-exclusions",
        scope,
        "fetch"
      ).url;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyCoverageExclusion[] = res.data.data;
      dispatch(fetchBusinessPolicyCoverageExclusionsListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyCoverageExclusionsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Coverage Exclusions binder section could not be loaded. Please retry.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinessPolicyCoverageExclusionAsync =
  (
    coverageExclusion: IBusinessPolicyCoverageExclusion,
    onCallback: (isSuccess: boolean, data?: IBusinessPolicyCoverageExclusion) => void,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    // Feature: lead-binder-business-tabs
    // Save-in-progress guard (Requirement 3.8): while a lead-scoped save is in flight,
    // reject a duplicate save for the same lead until it resolves. The customer-policy
    // path is untouched and does not consult this guard.
    if (leadId) {
      if (leadCoverageExclusionSaveInFlight) {
        onCallback(false);
        return;
      }
      leadCoverageExclusionSaveInFlight = true;
    }
    try {
      dispatch(saveLoaderProgress());

      if (leadId) {
        // Lead scope: persist with lead_id and NO customer_policy_id (Requirements 3.2, 3.4).
        const { url } = buildBinderRequest(
          "coverage-exclusions",
          { kind: "lead", leadId },
          "save"
        );
        const { customer_policy_id, customer_id, ...rest } =
          coverageExclusion as any;
        const res = await api.post(url, {
          ...rest,
          lead_id: leadId,
        });
        dispatch(
          showMessage({
            type: "success",
            message: "Coverage exclusion saved successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback(true, res.data.data);
        // Re-fetch the list by lead_id after a successful save (Requirements 3.3, 3.7).
        dispatch(
          fetchBusinessPolicyCoverageExclusionsListAsync(
            0,
            undefined,
            leadId
          )
        );
        return;
      }

      // Customer-policy scope. NOTE: the backend save route is singular
      // ("exclusion"), while the fetch route is plural ("exclusions").
      const res = await api.post(`/policy/edit-policy-business-coverage-exclusion`, {
        ...coverageExclusion,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Coverage exclusion saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Saving the Coverage Exclusions binder section failed. Your changes were not discarded — please retry.",
          displayAs: "snackbar",
        })
      );
      // On failure the entered data is retained (no state reset here) so the broker can
      // retry without re-entering input (Requirement 3.6).
      onCallback(false);
    } finally {
      if (leadId) {
        leadCoverageExclusionSaveInFlight = false;
      }
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_PROGRESS =
  "FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_PROGRESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_SUCCESS =
  "FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_SUCCESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_FAILED =
  "FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_FAILED";

export const fetchBusinessPolicyCoverageExclusionProgress = () =>
  action(FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_PROGRESS);
export const fetchBusinessPolicyCoverageExclusionSuccess = (
  data: IBusinessPolicyCoverageExclusion
) => action(FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_SUCCESS, { data });
export const fetchBusinessPolicyCoverageExclusionFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_COVERAGE_EXCLUSION_FAILED, { errorMessage });

export const fetchBusinessPolicyCoverageExclusionAsync =
  (
    customerPolicyId: number,
    coverageNumber: number,
    leadId?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyCoverageExclusionProgress());
      // Feature: lead-binder-business-tabs
      // In lead scope, resolve the base URL from the centralized endpoint map so the
      // request is keyed by lead_id and never by customer_policy_id (Requirements 3.1, 3.4).
      // The customer-policy branch below is unchanged (Requirement 4.2).
      const baseUrl = leadId
        ? buildBinderRequest(
            "coverage-exclusions",
            { kind: "lead", leadId },
            "fetch"
          ).url
        : `/policy/get-policy-business-coverage-exclusions?customer_policy_id=${customerPolicyId}`;
      const res = await api.get(
        `${baseUrl}&column=coverage_number&value=${coverageNumber}`
      );
      const data: IBusinessPolicyCoverageExclusion[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyCoverageExclusionSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyCoverageExclusionFailed(
            "Oops! We couldn't find any records associated with your coverage exclusion number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyCoverageExclusionFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_BUSINESS_POLICY_COVERAGE_EXCLUSION_STATE =
  "CLEAR_BUSINESS_POLICY_COVERAGE_EXCLUSION_STATE";
export const CLEAR_BUSINESS_POLICY_COVERAGE_EXCLUSION =
  "CLEAR_BUSINESS_POLICY_COVERAGE_EXCLUSION";

export const clearBusinessPolicyCoverageExclusionState = () =>
  action(CLEAR_BUSINESS_POLICY_COVERAGE_EXCLUSION_STATE);

export const clearBusinessPolicyCoverageExclusion = () =>
  action(CLEAR_BUSINESS_POLICY_COVERAGE_EXCLUSION);
